/**
 * Sync Inventory API Schemas
 *
 * Zod schemas and types for sync inventory endpoints.
 *
 * @layer features
 * @slice sync-inventory
 * @segment api
 */

import { z } from 'zod/v4';

export const SYNC_OPERATOR_DEFAULT_PAGE_SIZE = 10;
const syncOperatorPageSizeSchema = z.literal(SYNC_OPERATOR_DEFAULT_PAGE_SIZE);

export const syncInventoryStatusSchema = z.enum([
  'synced',
  'behind',
  'ahead',
  'pending',
  'not_onboarded',
  'error',
]);
export const syncInventoryCountStatusSchema = z.enum(['match', 'behind', 'ahead', 'unknown']);
export const syncInventorySyncMatchStatusSchema = z.enum(['synced', 'desynced']);

export const syncInventoryCountsSourceSchema = z.enum(['bubble_live', 'baseline_volume_json']);
export const syncInventorySyncModeSchema = z.enum(['auto', 'manual', 'off', 'not_applicable']);
export const syncInventoryReadinessStatusSchema = z.enum([
  'blocked',
  'schema_ready',
  'sync_ready',
  'validated',
  'not_applicable',
]);
export const syncInventoryComplexitySchema = z.enum([
  'no_option_no_related',
  'option_only',
  'related_only',
  'option_and_related',
  'not_applicable',
]);
export const syncInventoryOptionSetTypeSchema = z.enum(['enum', 'lookup']);
export const relationRefreshQueueStatusSchema = z.enum([
  'pending',
  'processing',
  'retry',
  'done',
  'dead',
]);

export const relationRefreshSweepRunStatusSchema = z.enum([
  'planned',
  'running',
  'paused',
  'completed',
  'failed',
  'cancelled',
]);

export const relationRefreshSweepRunModeSchema = z.enum(['pending', 'retry', 'all']);

const syncJobsV2RegistryIdentifierSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/);

export const syncJobsV2WorkerKeySchema = syncJobsV2RegistryIdentifierSchema;
export const syncJobsV2RuntimeStatusSchema = z.enum(['active', 'degraded', 'stale', 'disabled']);
export const syncJobsV2RuntimeListStatusSchema = z.enum(['active', 'failed', 'cancelled']);
export const syncJobsV2StaleReasonCodeSchema = z.enum(['no_snapshot_published', 'snapshot_stale']);
export const syncJobsV2DiagnosticSeveritySchema = z.enum(['warning', 'critical']);

const workerEvidenceSourceStatusSchema = z.enum(['fresh', 'stale', 'timeout', 'unavailable']);
const workerEvidenceTimestampSchema = z.iso.datetime().nullable();
const workerEvidenceReferenceSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('release'), releaseId: z.string().min(1).max(120) }),
  z.object({ type: z.literal('job'), jobId: z.string().min(1).max(120) }),
  z.object({ type: z.literal('run'), runId: z.string().min(1).max(120) }),
  z.object({ type: z.literal('machine'), machineId: z.string().min(1).max(120) }),
  z.object({ type: z.literal('correlation'), correlationId: z.string().min(1).max(120) }),
]);

export const workerHealthResponseSchema = z.object({
  data: z.object({
    workerKey: syncJobsV2WorkerKeySchema,
    workerDisplayName: z.string().min(1).max(100),
    isEnabled: z.boolean(),
    healthStatus: syncJobsV2RuntimeStatusSchema,
    reasonCodes: z.array(z.string().min(1).max(128)).max(20),
    heartbeat: z.object({
      status: workerEvidenceSourceStatusSchema,
      observedAt: workerEvidenceTimestampSchema,
      ageMs: z.number().int().nonnegative().nullable(),
      runtimeStatus: syncJobsV2RuntimeStatusSchema.nullable(),
      dbOk: z.boolean().nullable(),
      queueOk: z.boolean().nullable(),
    }),
    fly: z.object({
      status: workerEvidenceSourceStatusSchema,
      observedAt: workerEvidenceTimestampSchema,
      machines: z
        .array(
          z.object({
            machineId: z.string().min(1).max(128),
            machineName: z.string().max(160).nullable(),
            state: z.string().min(1).max(64),
            updatedAt: workerEvidenceTimestampSchema,
            checksStatus: z.enum(['passing', 'failing', 'unknown']),
          })
        )
        .max(10),
      truncated: z.boolean(),
    }),
  }),
  meta: z.object({
    workerKey: syncJobsV2WorkerKeySchema,
    status: z.enum(['available', 'partial']),
    fetchedAt: z.iso.datetime(),
    sources: z
      .array(
        z.object({
          sourceId: z.string().min(1).max(128),
          status: workerEvidenceSourceStatusSchema,
          observedAt: workerEvidenceTimestampSchema,
        })
      )
      .length(2),
  }),
});

export const workerDiagnosticsResponseSchema = z.object({
  data: z.object({
    items: z
      .array(
        z.object({
          diagnosticId: z.string().min(1).max(128),
          code: z.string().min(1).max(128),
          severity: syncJobsV2DiagnosticSeveritySchema,
          producerId: z.string().min(1).max(128),
          sourceIds: z.array(z.string().min(1).max(128)).max(20),
          scope: z.object({
            scopeType: z.enum(['worker', 'queue', 'schedule', 'machine', 'job', 'run', 'system']),
            scopeId: z.string().max(160).nullable(),
          }),
          ownerWorkerKey: syncJobsV2WorkerKeySchema,
          affectedWorkerKey: syncJobsV2WorkerKeySchema,
          observedAt: workerEvidenceTimestampSchema,
          message: z.string().min(1).max(500),
          details: z.record(
            z.string().max(64),
            z.union([z.string().max(500), z.number(), z.boolean(), z.null()])
          ),
          references: z
            .object({
              queueId: z.string().max(160).nullable().optional(),
              scheduleId: z.string().max(160).nullable().optional(),
              machineId: z.string().max(160).nullable().optional(),
              releaseId: z.string().max(160).nullable().optional(),
              jobId: z.string().max(160).nullable().optional(),
              runId: z.string().max(160).nullable().optional(),
              correlationId: z.string().max(160).nullable().optional(),
            })
            .nullable(),
        })
      )
      .max(100),
    truncated: z.boolean(),
  }),
  meta: z.object({
    workerKey: syncJobsV2WorkerKeySchema,
    status: z.enum(['available', 'partial', 'empty']),
    fetchedAt: z.iso.datetime(),
    sources: z
      .array(
        z.object({
          sourceId: z.string().min(1).max(128),
          status: workerEvidenceSourceStatusSchema,
          observedAt: workerEvidenceTimestampSchema,
        })
      )
      .max(50),
  }),
});

export const workerEventsResponseSchema = z.object({
  data: z.object({
    items: z
      .array(
        z.object({
          eventId: z.string().min(1).max(180),
          timestamp: z.iso.datetime(),
          kind: z.enum(['machine', 'application']),
          level: z.enum(['debug', 'info', 'warn', 'error']),
          code: z.string().min(1).max(128),
          message: z.string().min(1).max(500),
          workerKey: syncJobsV2WorkerKeySchema,
          machineId: z.string().min(1).max(128),
          releaseId: z.string().max(200).nullable(),
          jobId: z.string().max(200).nullable(),
          runId: z.string().max(200).nullable(),
          correlationId: z.string().max(200).nullable(),
        })
      )
      .max(100),
  }),
  meta: z.object({
    workerKey: syncJobsV2WorkerKeySchema,
    status: z.enum(['available', 'partial', 'empty']),
    fetchedAt: z.iso.datetime(),
    sources: z
      .array(
        z.object({
          sourceId: z.string().min(1).max(128),
          status: workerEvidenceSourceStatusSchema,
          observedAt: workerEvidenceTimestampSchema,
        })
      )
      .min(1)
      .max(3),
    nextCursor: z.string().min(1).max(2048).nullable(),
  }),
});

export const workerIncidentsResponseSchema = z.object({
  data: z.object({
    items: z
      .array(
        z.object({
          incidentId: z.string().min(1).max(64),
          workerKey: syncJobsV2WorkerKeySchema,
          producerId: z.string().min(1).max(160),
          diagnosticCode: z.string().min(1).max(160),
          scope: z.object({
            scopeType: z.enum(['worker', 'queue', 'machine', 'schedule']),
            scopeId: z.string().min(1).max(160),
          }),
          severity: syncJobsV2DiagnosticSeveritySchema,
          status: z.enum(['open', 'resolved']),
          summary: z.string().min(1).max(500),
          openedAt: z.iso.datetime(),
          lastObservedAt: z.iso.datetime().nullable(),
          resolvedAt: z.iso.datetime().nullable(),
          occurrenceCount: z.number().int().nonnegative(),
          reopenCount: z.number().int().nonnegative(),
          evidenceReferences: z.array(workerEvidenceReferenceSchema).max(20),
          createdAt: z.iso.datetime(),
          updatedAt: z.iso.datetime(),
          lastActivityAt: z.iso.datetime(),
        })
      )
      .max(100),
  }),
  meta: z.object({
    workerKey: syncJobsV2WorkerKeySchema,
    status: z.enum(['available', 'empty']),
    fetchedAt: z.iso.datetime(),
    sources: z
      .array(
        z.object({
          sourceId: z.literal('workerIncidents'),
          status: z.literal('fresh'),
          observedAt: z.iso.datetime(),
        })
      )
      .length(1),
    nextCursor: z.string().min(1).max(2048).nullable(),
  }),
});
export const syncJobsMediaStatusSchema = z.enum([
  'pending',
  'processing',
  'retry',
  'synced',
  'failed',
  'source_unavailable',
  'deleted',
]);
export const syncJobsMediaAssetStatusSchema = z.enum(['pending', 'ready', 'failed', 'deleted']);
export const syncJobsV2SyncRunStatusSchema = z.enum([
  'pending',
  'active',
  'paused',
  'done',
  'failed',
]);
export const syncJobsV2SyncRunEffectiveStatusSchema = z.enum([
  'pending',
  'active',
  'paused',
  'done',
  'failed',
  'resolved',
  'unresolved',
]);
export const syncJobsV2SyncRunModeSchema = z.enum(['full', 'delta', 'exact_ids']);
export const syncJobsV2SyncRunPhaseSchema = z.enum([
  'chunk',
  'user_postprocess_pending',
  'user_postprocess_active',
]);
export const syncInventoryEntrySchema = z.object({
  displayName: z.string(),
  apiName: z.string(),
  postgresTableName: z.string().nullable().optional(),
  optionSetType: syncInventoryOptionSetTypeSchema.nullable().optional(),
  optionsCount: z.number().int().nonnegative().nullable().optional(),
  remoteCount: z.number().nullable(),
  localCount: z.number().nullable(),
  sourceColumnsCount: z.number().nullable(),
  targetColumnsCount: z.number().nullable(),
  extraTargetColumnsCount: z.number().nullable(),
  columnParityStatus: z.enum(['unknown', 'matched', 'drift', 'error']).optional(),
  columnParitySummary: z.string().nullable().optional(),
  columnParityMeasuredAt: z.string().nullable().optional(),
  dataValidationStatus: z
    .enum(['OK', 'Run', 'Wait', 'Fix', 'Issue', 'Skip', 'Stale', 'None'])
    .optional(),
  dataValidationLastRunId: z.string().nullable().optional(),
  dataValidationLastCampaignId: z.string().nullable().optional(),
  dataValidationLastOutcome: z.string().nullable().optional(),
  dataValidationLastCheckedAt: z.string().nullable().optional(),
  dataValidationLastReportAvailable: z.boolean().optional(),
  dataValidationUpdatedAt: z.string().nullable().optional(),
  difference: z.number().nullable(),
  measuredAt: z.string().nullable(),
  lastCountMatchAt: z.string().nullable().optional(),
  lastCountMatchRemoteCount: z.number().nullable().optional(),
  lastCountMatchLocalCount: z.number().nullable().optional(),
  countStatus: syncInventoryCountStatusSchema.optional(),
  syncMatchStatus: syncInventorySyncMatchStatusSchema.optional(),
  syncMatchFreshnessHours: z.number().int().positive().optional(),
  countsDirty: z.boolean().optional(),
  countsDirtyAt: z.string().nullable().optional(),
  countsStale: z.boolean().optional(),
  countsSource: syncInventoryCountsSourceSchema.nullable(),
  syncStatus: syncInventoryStatusSchema,
  lastSyncAt: z.string().nullable(),
  lastSyncRecordCount: z.number().nullable(),
  isEnabled: z.boolean(),
  relationBacklogOpenCount: z.number().int().nonnegative().nullable(),
  relationBacklogPendingCount: z.number().int().nonnegative().nullable(),
  relationBacklogRetryCount: z.number().int().nonnegative().nullable(),
  relationBacklogProcessingCount: z.number().int().nonnegative().nullable(),
  nearRealtimeSyncEnabled: z.boolean(),
  syncMode: syncInventorySyncModeSchema,
  rolloutOrder: z.number().int().nullable(),
  readinessStatus: syncInventoryReadinessStatusSchema,
  blockingReason: z.string().nullable(),
  complexity: syncInventoryComplexitySchema,
  lastErrorMessage: z.string().nullable(),
  lastErrorAt: z.string().nullable(),
  activeSyncJobFamily: z.enum(['full', 'delta', 'reconcile', 'other']).nullable(),
  activeSyncJobStatus: z.enum(['started', 'in_progress']).nullable(),
  relationBacklogTotalCount: z.number().int().nonnegative().nullable().optional(),
  relationBacklogDoneCount: z.number().int().nonnegative().nullable().optional(),
  relationBacklogDeadCount: z.number().int().nonnegative().nullable().optional(),
  snapshotUpdatedAt: z.string().nullable().optional(),
  isStale: z.boolean().optional(),
  syncLockedReason: z.string().nullable().optional(),
  isSyncRunning: z.boolean().optional(),
});

export const syncDataTypesInventoryV2SortBySchema = z.enum([
  'displayName',
  'remoteCount',
  'localCount',
  'difference',
  'complexity',
  'syncStatus',
  'measuredAt',
  'lastSyncAt',
  'isEnabled',
  'syncMode',
  'syncMatch',
  'nearRealtimeSyncEnabled',
  'relationTotalCount',
  'snapshotUpdatedAt',
]);

export const syncDataTypesInventoryV2ListEntrySchema = z.object({
  displayName: z.string(),
  apiName: z.string(),
  postgresTableName: z.string().nullable(),
  syncStatus: syncInventoryStatusSchema,
  countStatus: syncInventoryCountStatusSchema,
  syncMatchStatus: syncInventorySyncMatchStatusSchema,
  syncMatchFreshnessHours: z.number().int().positive(),
  isEnabled: z.boolean(),
  syncMode: z.enum(['auto', 'manual', 'off']),
  nearRealtimeSyncEnabled: z.boolean(),
  complexity: syncInventoryComplexitySchema,
  remoteCount: z.number().nullable(),
  localCount: z.number().nullable(),
  sourceColumnsCount: z.number().nullable(),
  targetColumnsCount: z.number().nullable(),
  extraTargetColumnsCount: z.number().nullable(),
  columnParityStatus: z.enum(['unknown', 'matched', 'drift', 'error']),
  columnParitySummary: z.string().nullable(),
  columnParityMeasuredAt: z.string().nullable(),
  dataValidationStatus: z.enum(['OK', 'Run', 'Wait', 'Fix', 'Issue', 'Skip', 'Stale', 'None']),
  dataValidationLastRunId: z.string().nullable(),
  dataValidationLastCampaignId: z.string().nullable(),
  dataValidationLastOutcome: z.string().nullable(),
  dataValidationLastCheckedAt: z.string().nullable(),
  dataValidationLastReportAvailable: z.boolean(),
  dataValidationUpdatedAt: z.string().nullable(),
  difference: z.number().nullable(),
  measuredAt: z.string().nullable(),
  lastCountMatchAt: z.string().nullable(),
  lastCountMatchRemoteCount: z.number().nullable(),
  lastCountMatchLocalCount: z.number().nullable(),
  countsDirty: z.boolean(),
  countsDirtyAt: z.string().nullable(),
  countsStale: z.boolean(),
  lastSyncAt: z.string().nullable(),
  lastSyncRecordCount: z.number().nullable(),
  relationsByStatus: z.object({
    total: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    processing: z.number().int().nonnegative(),
    retry: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
    dead: z.number().int().nonnegative(),
  }),
  isSyncRunning: z.boolean(),
  activeSyncJobFamily: z.enum(['full', 'delta', 'reconcile', 'other']).nullable(),
  activeSyncJobStatus: z.enum(['started', 'in_progress']).nullable(),
  syncLockedReason: z.string().nullable(),
  snapshotUpdatedAt: z.string().nullable(),
  isStale: z.boolean(),
});

export const syncDataTypesInventoryV2ListResponseSchema = z.object({
  items: z.array(syncDataTypesInventoryV2ListEntrySchema),
  page: z.number().int().min(1),
  pageSize: syncOperatorPageSizeSchema,
  totalItems: z.number().int().nonnegative(),
  totalPages: z.number().int().min(1),
  snapshotStaleThresholdMinutes: z.number().int().positive(),
  syncMatchFreshnessHours: z.number().int().positive(),
  fetchedAt: z.string(),
});

export const syncDataTypesInventoryV2SummaryResponseSchema = z.object({
  totalItems: z.number().int().nonnegative(),
  syncedCount: z.number().int().nonnegative(),
  behindCount: z.number().int().nonnegative(),
  pendingCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
  syncedRatio: z.object({
    label: z.string(),
    syncedCount: z.number().int().nonnegative(),
    totalCount: z.number().int().nonnegative(),
    percentSynced: z.number().nonnegative(),
  }),
  syncModeDistribution: z.object({
    auto: z.number().int().nonnegative(),
    manual: z.number().int().nonnegative(),
    off: z.number().int().nonnegative(),
  }),
  lastCount: z.string().nullable(),
  lastDeltaSync: z.string().nullable(),
  snapshotUpdatedAt: z.string().nullable(),
  snapshotStaleThresholdMinutes: z.number().int().positive(),
  syncMatchFreshnessHours: z.number().int().positive(),
  isStale: z.boolean(),
  fetchedAt: z.string(),
});

export const syncInventorySummarySchema = z.object({
  totalDataTypes: z.number(),
  syncedCount: z.number(),
  pendingCount: z.number(),
  behindCount: z.number(),
  errorCount: z.number(),
  syncPercentage: z.number(),
  lastFullSync: z.string().nullable(),
  nextScheduledSync: z.string().nullable(),
});

export const syncInventoryKindSummarySchema = z.object({
  kind: z.enum(['data_type', 'option_set']),
  totalItems: z.number().int().nonnegative(),
  syncedCount: z.number().int().nonnegative(),
  behindCount: z.number().int().nonnegative(),
  pendingCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
  optionSetEnumCount: z.number().int().nonnegative().nullable().optional(),
  optionSetLookupCount: z.number().int().nonnegative().nullable().optional(),
  relationQueueOpenCount: z.number().int().nonnegative().nullable().optional(),
  relationQueuePendingCount: z.number().int().nonnegative().nullable().optional(),
  relationQueueRetryCount: z.number().int().nonnegative().nullable().optional(),
  relationQueueProcessingCount: z.number().int().nonnegative().nullable().optional(),
  relationQueueLastUpdatedAt: z.string().nullable().optional(),
  lastCount: z.string().nullable(),
  lastDeltaSync: z.string().nullable(),
  fetchedAt: z.string(),
});

export const relationRefreshQueueV2SweepStatusSchema = z.enum(['pending', 'retry']);

export const relationRefreshQueueTrustStatusSchema = z.enum([
  'trusted',
  'stale',
  'recounting',
  'failed',
  'unknown',
]);

export const relationRefreshQueueV2ListSortBySchema = z.enum([
  'displayName',
  'ownerTable',
  'queueTotalCount',
  'pendingCount',
  'processingCount',
  'retryCount',
  'deadCount',
  'pressure',
  'exactRecountStatus',
  'snapshotUpdatedAt',
]);

export const relationRefreshQueueItemsLaneSchema = z.enum(['regular', 'bulk', 'dead_repair']);
export const relationRefreshQueueItemsRepairStateSchema = z.literal('unsafe');

export const relationRefreshQueueV2EntrySchema = z.object({
  ownerTable: z.string(),
  bubbleKey: z.string(),
  displayName: z.string(),
  entitiesCount: z.number().int().nullable(),
  queueTotalCount: z.number().int().nonnegative(),
  pendingCount: z.number().int().nonnegative(),
  processingCount: z.number().int().nonnegative(),
  retryCount: z.number().int().nonnegative(),
  doneCount: z.number().int().nonnegative(),
  deadCount: z.number().int().nonnegative(),
  snapshotUpdatedAt: z.string().nullable(),
  exactRecountStatus: z
    .enum(['idle', 'refreshing', 'failed', 'unknown'])
    .optional()
    .default('unknown'),
  exactRecountStartedAt: z.string().nullable().optional().default(null),
  exactRecountFinishedAt: z.string().nullable().optional().default(null),
  exactRecountCompletedAt: z.string().nullable().optional().default(null),
  exactRecountDurationMs: z.number().int().nonnegative().nullable().optional().default(null),
  exactRecountReasonCode: z
    .enum([
      'TIMEOUT',
      'CONCURRENT_DELTA',
      'COUNTER_DRIFT_NEGATIVE',
      'RELATION_DIAGNOSTIC_DELTA_INVALID',
      'ERROR',
      'UNKNOWN',
    ])
    .nullable()
    .optional()
    .default(null),
  counterDeltaUpdatedAt: z.string().nullable().optional().default(null),
  snapshotAgeMs: z.number().int().nonnegative().nullable().optional().default(null),
  trustStatus: relationRefreshQueueTrustStatusSchema.optional().default('unknown'),
  canRunSweep: z.boolean(),
  sweepLockedReason: z.string().nullable(),
  allowedSweepStatuses: z.array(relationRefreshQueueV2SweepStatusSchema),
  defaultSweepStatus: z.literal('pending'),
});

export const relationRefreshQueueV2ListResponseSchema = z.object({
  items: z.array(relationRefreshQueueV2EntrySchema),
  page: z.number().int().min(1),
  pageSize: syncOperatorPageSizeSchema,
  totalItems: z.number().int().nonnegative(),
  totalPages: z.number().int().min(1),
  fetchedAt: z.string().optional().default(''),
});

const syncDiagnosticsBucketSchema = z.object({
  key: z.string(),
  count: z.number().int().nonnegative(),
});

export const syncRelationQueueLiveMetricSchema = z.enum([
  'due',
  'ageBuckets',
  'oldestDue',
  'backoffBuckets',
]);

export const syncRelationQueueDiagnosticsResponseSchema = z.object({
  fetchedAt: z.string(),
  sectionStatus: z.enum(['ok', 'partial', 'timeout', 'error', 'not_available']),
  data: z.object({
    statusCounts: z.object({
      pending: z.number().int().nonnegative(),
      processing: z.number().int().nonnegative(),
      retry: z.number().int().nonnegative(),
      done: z.number().int().nonnegative(),
      dead: z.number().int().nonnegative(),
    }),
    deadRepair: z
      .object({
        status: z.enum(['available', 'partial', 'stale', 'unavailable']),
        observedAt: z.string().nullable(),
        gauges: z.object({
          evidenceStatus: z.enum(['fresh', 'stale', 'timeout', 'unavailable']),
          observedAt: z.string().nullable(),
          deadCount: z.number().int().nonnegative().nullable(),
          processingRows: z.number().int().nonnegative().nullable(),
          exactIdRetryRows: z.number().int().nonnegative().nullable(),
          unsafeRows: z.number().int().nonnegative().nullable(),
        }),
        exactReadiness: z.object({
          evidenceStatus: z.enum(['fresh', 'stale', 'timeout', 'unavailable']),
          reason: z.enum(['sync_disabled', 'refresh_overdue']).nullable(),
          observedAt: z.string().nullable(),
          deadReadyRows: z.number().int().nonnegative().nullable(),
          oldestDeadAt: z.string().nullable(),
          oldestReadyAt: z.string().nullable(),
          fastGaugeDrift: z
            .object({
              deadCount: z.number().int().nullable(),
              processingRows: z.number().int().nullable(),
              exactIdRetryRows: z.number().int().nullable(),
              unsafeRows: z.number().int().nullable(),
            })
            .nullable(),
        }),
        jobActivity: z.object({
          evidenceStatus: z.enum(['fresh', 'stale', 'timeout', 'unavailable']),
          status: z.enum(['available', 'stale', 'unavailable']),
          observedAt: z.string().nullable(),
          activeJobs: z.number().int().nonnegative().nullable(),
          queuedContinuations: z.number().int().nonnegative().nullable(),
        }),
      })
      .optional(),
    trustState: z.object({
      counterEvidence: z
        .object({
          status: z.enum(['verified', 'unverified', 'partial', 'unavailable']),
          lastVerifiedAt: z.string().nullable(),
        })
        .optional(),
      snapshotRows: z.number().int().nonnegative(),
      snapshotFresh: z.boolean(),
      oldestSnapshotAgeSeconds: z.number().int().nonnegative().nullable(),
      newestSnapshotAt: z.string().nullable(),
      failedRows: z.number().int().nonnegative(),
      refreshingRows: z.number().int().nonnegative(),
      retryEligibleFailedRows: z.number().int().nonnegative(),
      nextRetryEligibleAt: z.string().nullable(),
    }),
    readiness: z.object({
      dueRows: z.number().int().nonnegative().nullable(),
      deadRows: z.number().int().nonnegative(),
      retryRows: z.number().int().nonnegative(),
    }),
    settings: z.object({
      status: z.enum(['available', 'partial', 'unavailable', 'not_available']),
      enabled: z.boolean().nullable(),
      batchSize: z.number().int().nonnegative().nullable(),
      rateLimitPerMinute: z.number().int().nonnegative().nullable(),
      timeBudgetMs: z.number().int().nonnegative().nullable(),
    }),
    workerFreshness: z.object({
      status: z.literal('deprecated').optional(),
      lastDeadRepairFinishedAt: z.string().nullable(),
      completed24h: z.number().int().nonnegative().nullable(),
      failed24h: z.number().int().nonnegative().nullable(),
    }),
  }),
});

export const syncRelationQueueDiagnosticsReadoutResponseSchema = z
  .object({
    selection: z.object({
      mode: z.enum(['explicit', 'implicit']),
      selectedSections: z.array(z.enum(['dirty', 'exactId', 'relationQueue', 'tableQueue'])),
    }),
    sections: z.object({
      relationQueue: syncRelationQueueDiagnosticsResponseSchema,
    }),
  })
  .transform((readout) => readout.sections.relationQueue);

export const syncRelationQueueLiveMetricResponseSchema = z.object({
  data: z.union([
    z.object({
      due: z.number().int().nonnegative(),
      calculatedAt: z.string(),
      durationMs: z.number().int().nonnegative(),
    }),
    z.object({
      ageBuckets: z.array(syncDiagnosticsBucketSchema),
      calculatedAt: z.string(),
      durationMs: z.number().int().nonnegative(),
    }),
    z.object({
      oldestDue: z.string().nullable(),
      calculatedAt: z.string(),
      durationMs: z.number().int().nonnegative(),
    }),
    z.object({
      backoffBuckets: z.array(syncDiagnosticsBucketSchema),
      calculatedAt: z.string(),
      durationMs: z.number().int().nonnegative(),
    }),
  ]),
});

export const relationRefreshQueueItemsResponseSchema = z.object({
  items: z.array(
    z.object({
      queueRowId: z.uuid(),
      ownerTable: z.string(),
      status: relationRefreshQueueStatusSchema,
      lane: relationRefreshQueueItemsLaneSchema.nullable(),
      attempts: z.number().int().nonnegative(),
      nextRetryAt: z.string(),
      dueAt: z.string(),
      leaseExpiresAt: z.string().nullable(),
      lastWorkerKind: z.string().nullable(),
      reasonCode: z.string().nullable(),
      dependencyState: z.string().nullable(),
      createdAt: z.string(),
      updatedAt: z.string(),
      repairState: relationRefreshQueueItemsRepairStateSchema.nullable(),
      repairAttemptCount: z.number().int().nonnegative(),
      repairNextCheckAt: z.string().nullable(),
    })
  ),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
  limit: z.number().int().min(1).max(100),
  evidenceStatus: z.literal('available'),
});

const syncOpsSnapshotDeadRepairProjectionSchema = z
  .discriminatedUnion('supportStatus', [
    z.object({
      supportStatus: z.literal('supported'),
      status: z.enum(['green', 'yellow', 'red', 'disabled']),
      evidenceStatus: z
        .enum(['available', 'partial', 'not_available'])
        .optional()
        .default('not_available'),
    }),
    z.object({
      supportStatus: z.literal('not_supported'),
      status: z.literal('unknown'),
    }),
  ])
  .transform((value) => ({
    supportStatus: value.supportStatus,
    status: value.status,
    evidenceStatus:
      value.supportStatus === 'not_supported' ? 'not_available' : value.evidenceStatus,
  }));

const syncOpsSnapshotSourceNames = [
  'workerInventory',
  'syncSettings',
  'jobsSnapshot',
  'dataTypesInventory',
] as const;

export const syncOpsSnapshotResponseSchema = z.object({
  collection: z.object({
    partial: z.boolean(),
    sources: z
      .array(
        z.object({
          name: z.enum(syncOpsSnapshotSourceNames),
          status: z.enum(['fresh', 'stale', 'timeout', 'unavailable']),
          observedAt: z.string(),
        })
      )
      .length(syncOpsSnapshotSourceNames.length)
      .superRefine((sources, context) => {
        const names = new Set(sources.map((source) => source.name));
        if (syncOpsSnapshotSourceNames.some((name) => !names.has(name))) {
          context.addIssue({
            code: 'custom',
            message: 'Collection sources must contain each published source exactly once',
          });
        }
      }),
  }),
  deadRepair: syncOpsSnapshotDeadRepairProjectionSchema,
});

export const relationRefreshSweepRunListSortBySchema = z.enum([
  'updatedAt',
  'createdAt',
  'startedAt',
  'completedAt',
  'status',
  'mode',
  'ownerTableFilter',
  'totalItems',
  'processedItems',
  'doneItems',
  'retryItems',
]);

export const relationRefreshSweepRunEntrySchema = z.object({
  id: z.uuid(),
  mode: relationRefreshSweepRunModeSchema,
  status: relationRefreshSweepRunStatusSchema,
  ownerTableFilter: z.string().nullable(),
  batchSize: z.number().int().nonnegative(),
  leaseMs: z.number().int().nonnegative(),
  pauseBetweenBatchesMs: z.number().int().nonnegative(),
  totalItems: z.number().int().nonnegative(),
  processedItems: z.number().int().nonnegative(),
  doneItems: z.number().int().nonnegative(),
  retryItems: z.number().int().nonnegative(),
  deadItems: z.number().int().nonnegative(),
  ownerMissingItems: z.number().int().nonnegative(),
  failedItems: z.number().int().nonnegative(),
  skippedItems: z.number().int().nonnegative(),
  lastError: z.string().nullable(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export const relationRefreshSweepRunListResponseSchema = z.object({
  items: z.array(relationRefreshSweepRunEntrySchema),
  page: z.number().int().min(1),
  pageSize: syncOperatorPageSizeSchema,
  totalItems: z.number().int().nonnegative(),
  totalPages: z.number().int().min(1),
  fetchedAt: z.string(),
});

export const syncJobActionResponseSchema = z.object({
  data: z.object({
    syncId: z.uuid().optional(),
    rowId: z.uuid().optional(),
    runId: z.uuid().optional(),
    action: z.enum(['stop', 'restart', 'delete', 'start', 'fail']),
    outcome: z.enum(['accepted', 'noop']),
    message: z.string(),
    jobId: z.string().nullable().optional(),
    deletedItemCount: z.number().int().nonnegative().optional(),
    deduped: z.boolean().optional(),
    reason: z.enum(['in_progress', 'queued']).optional(),
  }),
});

export const relationRefreshSweepRunStartResponseSchema = z
  .object({
    data: z
      .object({
        runId: z.uuid(),
        action: z.literal('start'),
        outcome: z.literal('accepted'),
        mode: z.enum(['pending', 'retry', 'all']),
        ownerTableFilter: z.string().min(1).max(160).nullable(),
        message: z.string().min(1).max(240),
      })
      .strict(),
  })
  .strict();

export const relationRefreshSweepRunStopResponseSchema = z
  .object({
    data: z
      .object({
        runId: z.uuid(),
        action: z.literal('stop'),
        outcome: z.enum(['accepted', 'noop']),
        message: z.string().min(1).max(240),
      })
      .strict(),
  })
  .strict();

export const relationRefreshSweepRunRestartResponseSchema = z
  .object({
    data: z
      .object({
        runId: z.uuid(),
        action: z.literal('restart'),
        outcome: z.literal('accepted'),
        message: z.string().min(1).max(240),
      })
      .strict(),
  })
  .strict();

export const relationRefreshSweepRunDeleteResponseSchema = z
  .object({
    data: z
      .object({
        runId: z.uuid(),
        action: z.literal('delete'),
        outcome: z.literal('accepted'),
        deletedItemCount: z.number().int().nonnegative(),
        message: z.string().min(1).max(240),
      })
      .strict(),
  })
  .strict();

export const syncJobsV2ListEntrySchema = z.object({
  workerKey: syncJobsV2WorkerKeySchema,
  workerDisplayName: z.string(),
  workerRuntimeStatus: syncJobsV2RuntimeStatusSchema,
  statusReason: z.string().nullable(),
  isEnabled: z.boolean(),
  scheduleSummaryLabel: z.string(),
  activeJobsCountLive: z.number().int().nonnegative(),
  queuedJobsCountLive: z.number().int().nonnegative(),
  failedJobs7d: z.number().int().nonnegative(),
  cancelledJobs7d: z.number().int().nonnegative(),
  windowStartedAt: z.string(),
  windowEndedAt: z.string(),
  snapshotTakenAt: z.string(),
  lastFailedJobAt: z.string().nullable().optional(),
  lastErrorCode: z.string().nullable().optional(),
  lastErrorMessage: z.string().nullable().optional(),
  machineStatus: z
    .enum([
      'started',
      'starting',
      'stopping',
      'restarting',
      'updating',
      'stopped',
      'suspended',
      'failed',
      'missing',
      'unknown',
    ])
    .optional(),
  flyChecks: z.enum(['passing', 'failing', 'unknown']).optional(),
  machineUpdatedAt: z.string().nullable().optional(),
  flyObservedAt: z.string().nullable().optional(),
  flyInventoryStatus: z.enum(['fresh', 'stale', 'unavailable']).optional(),
});

export const syncWorkersResponseSchema = z.object({
  data: z.object({
    items: z.array(
      syncJobsV2ListEntrySchema.extend({
        links: z.object({
          self: z.string().startsWith('/api/sync/workers/'),
          diagnostics: z.string().startsWith('/api/sync/workers/'),
          events: z.string().startsWith('/api/sync/workers/'),
          incidents: z.string().startsWith('/api/sync/workers/'),
        }),
      })
    ),
    total: z.number().int().nonnegative(),
  }),
  meta: z.object({
    status: z.enum(['available', 'partial', 'empty', 'unavailable']),
    fetchedAt: z.iso.datetime(),
    sources: z.array(
      z.object({
        sourceId: z.enum(['jobsSnapshot', 'flyInventory']),
        status: workerEvidenceSourceStatusSchema,
        observedAt: workerEvidenceTimestampSchema,
      })
    ),
  }),
});

const syncActiveOperationsSummarySchema = z.object({
  activeCount: z.number().int().nonnegative(),
  liveActiveCount: z.number().int().nonnegative(),
  staleLedgerCount: z.number().int().nonnegative(),
  activeCategoryLabels: z.array(z.string()),
  activeCategoryCounts: z.array(z.number().int().nonnegative()),
  longRunningCount: z.number().int().nonnegative(),
  criticalLongRunningCount: z.number().int().nonnegative(),
  leaseExpiredCount: z.number().int().nonnegative(),
  retryRecentCount: z.number().int().nonnegative(),
  validationRelatedCount: z.number().int().nonnegative(),
  latestActivityAt: z.string().nullable(),
  latestStaleLedgerAt: z.string().nullable(),
  latestRetryActivityAt: z.string().nullable(),
});

export const syncJobsV2SummaryResponseSchema = z.object({
  snapshotBatchId: z.string().nullable(),
  activeJobsCountLive: z.number().int().nonnegative(),
  queuedJobsCountLive: z.number().int().nonnegative(),
  failedJobs7d: z.number().int().nonnegative(),
  cancelledJobs7d: z.number().int().nonnegative(),
  isStale: z.boolean(),
  staleReasonCode: syncJobsV2StaleReasonCodeSchema.nullable(),
  staleLagMinutes: z.number().int().positive().nullable(),
  snapshotTakenAt: z.string().nullable(),
  fetchedAt: z.string(),
  snapshot: z.object({
    batchId: z.string().nullable(),
    activeCount: z.number().int().nonnegative(),
    queuedCount: z.number().int().nonnegative(),
    takenAt: z.string().nullable(),
    isStale: z.boolean(),
    staleReasonCode: syncJobsV2StaleReasonCodeSchema.nullable(),
    staleLagMinutes: z.number().int().positive().nullable(),
  }),
  liveOperations: syncActiveOperationsSummarySchema,
});

export const syncJobsMediaListEntrySchema = z.object({
  id: z.uuid(),
  ownerTable: z.string(),
  ownerId: z.uuid(),
  slotKey: z.string(),
  syncStatus: syncJobsMediaStatusSchema,
  assetStatus: syncJobsMediaAssetStatusSchema.nullable(),
  sourceUrl: z.string(),
  uploadedBytes: z.number().int().nonnegative(),
  attempts: z.number().int().nonnegative(),
  activityAt: z.string().nullable(),
  progressUpdatedAt: z.string().nullable(),
  processingStartedAt: z.string().nullable(),
  leaseExpiresAt: z.string().nullable(),
  nextRetryAt: z.string().nullable(),
  uploadedAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  lastIssue: z.string().nullable(),
});

export const syncJobsMediaListResponseSchema = z.object({
  items: z.array(syncJobsMediaListEntrySchema),
  page: z.number().int().min(1),
  pageSize: syncOperatorPageSizeSchema,
  totalItems: z.number().int().nonnegative(),
  totalPages: z.number().int().min(1),
  fetchedAt: z.string(),
});

export const syncJobsV2RuntimeListEntrySchema = z.object({
  jobId: z.string(),
  jobName: z.string(),
  queueName: z.string(),
  workerKey: syncJobsV2WorkerKeySchema,
  workerDisplayName: z.string(),
  status: syncJobsV2RuntimeListStatusSchema,
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  durationMs: z.number().int().nullable(),
  attempt: z.number().int().nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  resultPayload: z.record(z.string(), z.unknown()).nullable(),
});

export const syncJobsV2RuntimeListResponseSchema = z.object({
  status: syncJobsV2RuntimeListStatusSchema,
  items: z.array(syncJobsV2RuntimeListEntrySchema),
  page: z.number().int().min(1),
  pageSize: syncOperatorPageSizeSchema,
  totalItems: z.number().int().nonnegative(),
  totalPages: z.number().int().min(1),
  fetchedAt: z.string(),
});

export const syncJobRunsRuntimeResponseSchema = syncJobsV2RuntimeListResponseSchema.extend({
  projection: z.literal('runtime'),
});

export const syncJobsV2SyncRunEntrySchema = z.object({
  id: z.uuid(),
  bubbleKey: z.string(),
  tableName: z.string(),
  tableLabel: z.string(),
  runMode: syncJobsV2SyncRunModeSchema,
  runModeLabel: z.string(),
  status: syncJobsV2SyncRunStatusSchema,
  statusLabel: z.string(),
  effectiveStatus: syncJobsV2SyncRunEffectiveStatusSchema,
  effectiveStatusLabel: z.string(),
  executionPhase: syncJobsV2SyncRunPhaseSchema,
  phaseLabel: z.string(),
  chunkSize: z.number().int().nonnegative(),
  cursor: z.number().int().nullable(),
  attemptCount: z.number().int().nonnegative(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  lastProgressAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  activityAt: z.string().nullable(),
  lastModifiedSeen: z.string().nullable(),
  deltaWindowStart: z.string().nullable(),
  requestedOverlapMs: z.number().int().nonnegative().nullable(),
  appliedOverlapMs: z.number().int().nonnegative().nullable(),
  overlapDefaultUsed: z.boolean().nullable(),
  pendingLastModifiedSeen: z.string().nullable(),
  progressLabel: z.string(),
  checkpointLabel: z.string(),
  durationLabel: z.string(),
  lastIssue: z
    .string()
    .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/u)
    .nullable(),
  lastIssueCode: z
    .string()
    .regex(/^SYNC-\d{4}$/u)
    .nullable()
    .optional(),
  lastIssueText: z.string().max(240).nullable().optional(),
  lastIssueSource: z.enum(['postgres', 'sync']).nullable().optional(),
  lastIssueProviderCode: z
    .string()
    .regex(/^(?:[0-9A-Z]{5}|ECONNRESET|ECONNREFUSED|ETIMEDOUT|EPIPE)$/u)
    .nullable()
    .optional(),
  lastIssueStage: z
    .enum(['identity_lock', 'queue', 'chunk', 'postprocess', 'not_found_classification', 'parity'])
    .nullable()
    .optional(),
  lastIssueOperation: z
    .enum([
      'acquire_identity_lock',
      'register_chunk_failure',
      'dispatch_postprocess',
      'run_chunk',
      'read_postprocess_payload',
      'run_postprocess',
      'slice_payload',
      'load_rows',
      'runtime_build',
      'typed_update',
      'relation_enqueue',
      'media_enqueue',
      'user_postprocess',
      'bubble_mutation_convergence',
      'accounting',
      'continuation',
      'verify_source_presence',
      'verify_bubble',
      'dependency_cleanup',
      'delete_local',
      'persist_delete_outcome',
      'pause_queue',
      'terminalize_queue',
      'terminal_parity',
    ])
    .nullable()
    .optional(),
  lastIssueRetryability: z.enum(['retryable', 'terminal', 'none']).nullable().optional(),
  lastIssueCorrelationId: z.string().min(1).max(160).nullable().optional(),
  lastIssueErrorKind: z
    .enum([
      'type_error',
      'range_error',
      'syntax_error',
      'reference_error',
      'configuration_error',
      'database_error',
      'transport_error',
      'unknown',
    ])
    .nullable()
    .optional(),
  lastIssueErrorDetail: z
    .enum([
      'null_property_access',
      'undefined_property_access',
      'not_callable',
      'not_iterable',
      'undefined_identifier',
      'invalid_date',
      'invalid_array_length',
      'stack_overflow',
      'circular_json',
      'bigint_json',
    ])
    .nullable()
    .optional(),
  lastIssueErrorSite: z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,79}$/u)
    .nullable()
    .optional(),
  lastIssueHttpStatus: z.number().int().min(100).max(599).nullable().optional(),
  exactIdDisposition: z
    .object({
      status: z.enum(['pending', 'active', 'synced', 'not_found', 'failed', 'quarantined']),
      reasonCode: z.string().min(1).max(80).nullable(),
      quarantineReason: z.string().min(1).max(80).nullable(),
      classifierStatus: z.string().min(1).max(80).nullable(),
      classifierOutcomeCode: z.string().min(1).max(80).nullable(),
      classifierReasonCode: z.string().min(1).max(80).nullable(),
      nextRetryAt: z.string().nullable(),
    })
    .nullable()
    .optional(),
});

export const syncJobsV2SyncRunsResponseSchema = z.object({
  items: z.array(syncJobsV2SyncRunEntrySchema),
  page: z.number().int().min(1),
  pageSize: syncOperatorPageSizeSchema,
  totalItems: z.number().int().nonnegative(),
  totalPages: z.number().int().min(1),
  fetchedAt: z.string(),
});

export const syncInventoryDataTypeOptionSchema = z.object({
  displayName: z.string(),
  apiName: z.string(),
  isEnabled: z.boolean(),
});

export const syncInventoryDataTypeOptionsResponseSchema = z.object({
  items: z.array(syncInventoryDataTypeOptionSchema),
  fetchedAt: z.string(),
});

export const syncInventoryResponseSchema = z.object({
  dataTypes: z.array(syncInventoryEntrySchema),
  optionSets: z.array(syncInventoryEntrySchema),
  summary: syncInventorySummarySchema,
  fetchedAt: z.string(),
});

export const syncInventoryListSortBySchema = z.enum([
  'displayName',
  'remoteCount',
  'localCount',
  'difference',
  'complexity',
  'syncStatus',
  'measuredAt',
  'lastSyncAt',
  'isEnabled',
  'syncMode',
  'nearRealtimeSyncEnabled',
  'relationBacklogOpenCount',
]);

export const syncInventoryListSortDirSchema = z.enum(['asc', 'desc']);

export const syncInventoryListResponseSchema = z.object({
  items: z.array(syncInventoryEntrySchema),
  page: z.number().int().min(1),
  pageSize: syncOperatorPageSizeSchema,
  totalItems: z.number().int().nonnegative(),
  totalPages: z.number().int().min(1),
  fetchedAt: z.string(),
});

export const syncInventoryExtraTargetColumnSchema = z.object({
  columnName: z.string(),
  actualType: z.string(),
});

export const syncInventoryColumnParityFieldIssueSchema = z.object({
  bubbleFieldKey: z.string(),
  displayName: z.string(),
  expectedColumns: z.array(z.string()),
});

export const syncInventoryColumnParityPartialFieldIssueSchema =
  syncInventoryColumnParityFieldIssueSchema.extend({
    presentColumns: z.array(z.string()),
    missingColumns: z.array(z.string()),
  });

export const syncInventoryColumnParityDetailsSchema = z.object({
  missingInPostgres: z.array(syncInventoryColumnParityFieldIssueSchema),
  partialInPostgres: z.array(syncInventoryColumnParityPartialFieldIssueSchema),
  unmappedBubbleFields: z.array(
    z.object({
      bubbleFieldKey: z.string(),
      displayName: z.string(),
      type: z.string(),
    })
  ),
  extraInPostgres: z.array(
    z.object({
      columnName: z.string(),
    })
  ),
  lastError: z
    .object({
      measuredAt: z.string(),
      message: z.string(),
      attemptCount: z.number().int().nonnegative(),
      nextRetryAt: z.string().nullable(),
    })
    .nullable(),
});

export const syncInventoryColumnDetailsResponseSchema = z.object({
  displayName: z.string(),
  apiName: z.string(),
  tableName: z.string().nullable(),
  sourceColumnsCount: z.number().nullable(),
  targetColumnsCount: z.number().nullable(),
  extraTargetColumnsCount: z.number().nullable(),
  extraTargetColumns: z.array(syncInventoryExtraTargetColumnSchema),
  columnParityStatus: z.enum(['unknown', 'matched', 'drift', 'error']),
  columnParitySummary: z.string().nullable(),
  columnParityMeasuredAt: z.string().nullable(),
  columnParityDetails: syncInventoryColumnParityDetailsSchema,
  fetchedAt: z.string(),
});

export const syncInventoryOptionSetOptionSchema = z.object({
  display: z.string(),
  sourcePayload: z.unknown().nullable(),
});

export const syncInventoryOptionSetOptionsResponseSchema = z.object({
  items: z.array(syncInventoryOptionSetOptionSchema),
  page: z.number().int().min(1),
  pageSize: syncOperatorPageSizeSchema,
  totalItems: z.number().int().nonnegative(),
  totalPages: z.number().int().min(1),
  fetchedAt: z.string(),
});

export const countAuditAcceptedResponseSchema = z.object({
  data: z.object({
    jobId: z.string(),
    auditId: z.string(),
    state: z.literal('queued'),
  }),
});

export const refreshColumnParityResponseSchema = z.object({
  data: z.object({
    jobId: z.string().nullable(),
    state: z.enum(['queued', 'deduped']),
  }),
});

export const deltaSyncResponseSchema = z.object({
  data: z.object({
    jobId: z.string().nullable().optional(),
    deduped: z.boolean().optional(),
    reason: z.enum(['in_progress', 'queued']).optional(),
    dirtySet: z.boolean().optional(),
  }),
});

export type SyncInventoryStatus = z.infer<typeof syncInventoryStatusSchema>;
export type SyncInventoryCountStatus = z.infer<typeof syncInventoryCountStatusSchema>;
export type SyncInventoryCountsSource = z.infer<typeof syncInventoryCountsSourceSchema>;
export type SyncInventorySyncMode = z.infer<typeof syncInventorySyncModeSchema>;
export type SyncInventorySyncMatchStatus = z.infer<typeof syncInventorySyncMatchStatusSchema>;
export type SyncInventoryReadinessStatus = z.infer<typeof syncInventoryReadinessStatusSchema>;
export type SyncInventoryComplexity = z.infer<typeof syncInventoryComplexitySchema>;
export type SyncInventoryOptionSetType = z.infer<typeof syncInventoryOptionSetTypeSchema>;
export type RelationRefreshQueueStatus = z.infer<typeof relationRefreshQueueStatusSchema>;
export type RelationRefreshSweepRunStatus = z.infer<typeof relationRefreshSweepRunStatusSchema>;
export type RelationRefreshSweepRunMode = z.infer<typeof relationRefreshSweepRunModeSchema>;
export type SyncJobsV2WorkerKey = z.infer<typeof syncJobsV2WorkerKeySchema>;
export type SyncJobsV2RuntimeStatus = z.infer<typeof syncJobsV2RuntimeStatusSchema>;
export type SyncJobsV2RuntimeListStatus = z.infer<typeof syncJobsV2RuntimeListStatusSchema>;
export type SyncJobsMediaStatus = z.infer<typeof syncJobsMediaStatusSchema>;
export type SyncJobsMediaAssetStatus = z.infer<typeof syncJobsMediaAssetStatusSchema>;
export type SyncJobsV2SyncRunStatus = z.infer<typeof syncJobsV2SyncRunStatusSchema>;
export type SyncJobsV2SyncRunEffectiveStatus = z.infer<
  typeof syncJobsV2SyncRunEffectiveStatusSchema
>;
export type SyncJobsV2SyncRunMode = z.infer<typeof syncJobsV2SyncRunModeSchema>;
export type SyncJobsV2SyncRunPhase = z.infer<typeof syncJobsV2SyncRunPhaseSchema>;
export type SyncInventoryEntry = z.infer<typeof syncInventoryEntrySchema>;
export type SyncInventoryExtraTargetColumn = z.infer<typeof syncInventoryExtraTargetColumnSchema>;
export type SyncInventorySummary = z.infer<typeof syncInventorySummarySchema>;
export type SyncInventoryKindSummary = z.infer<typeof syncInventoryKindSummarySchema>;
export type SyncDataTypesInventoryV2SortBy = z.infer<typeof syncDataTypesInventoryV2SortBySchema>;
export type SyncDataTypesInventoryV2ListEntry = z.infer<
  typeof syncDataTypesInventoryV2ListEntrySchema
>;
export type SyncDataTypesInventoryV2ListResponse = z.infer<
  typeof syncDataTypesInventoryV2ListResponseSchema
>;
export type SyncDataTypesInventoryV2SummaryResponse = z.infer<
  typeof syncDataTypesInventoryV2SummaryResponseSchema
>;
export type RelationRefreshQueueV2SweepStatus = z.infer<
  typeof relationRefreshQueueV2SweepStatusSchema
>;
export type RelationRefreshQueueTrustStatus = z.infer<typeof relationRefreshQueueTrustStatusSchema>;
export type RelationRefreshQueueV2ListSortBy = z.infer<
  typeof relationRefreshQueueV2ListSortBySchema
>;
export type RelationRefreshQueueItemsLane = z.infer<typeof relationRefreshQueueItemsLaneSchema>;
export type RelationRefreshQueueItemsRepairState = z.infer<
  typeof relationRefreshQueueItemsRepairStateSchema
>;
export type RelationRefreshQueueV2Entry = z.infer<typeof relationRefreshQueueV2EntrySchema>;
export type RelationRefreshQueueV2ListResponse = z.infer<
  typeof relationRefreshQueueV2ListResponseSchema
>;
export type RelationRefreshQueueItemsResponse = z.infer<
  typeof relationRefreshQueueItemsResponseSchema
>;
export type RelationRefreshQueueItem = RelationRefreshQueueItemsResponse['items'][number];
export type SyncRelationQueueLiveMetric = z.infer<typeof syncRelationQueueLiveMetricSchema>;
export type SyncRelationQueueLiveMetricResponse = z.infer<
  typeof syncRelationQueueLiveMetricResponseSchema
>;
export type SyncRelationQueueDiagnosticsResponse = z.infer<
  typeof syncRelationQueueDiagnosticsResponseSchema
>;
export type SyncOpsSnapshotResponse = z.infer<typeof syncOpsSnapshotResponseSchema>;
export type RelationRefreshSweepRunListSortBy = z.infer<
  typeof relationRefreshSweepRunListSortBySchema
>;
export type RelationRefreshSweepRunEntry = z.infer<typeof relationRefreshSweepRunEntrySchema>;
export type RelationRefreshSweepRunListResponse = z.infer<
  typeof relationRefreshSweepRunListResponseSchema
>;
export type SyncJobActionResponse = z.infer<typeof syncJobActionResponseSchema>;
export type RelationRefreshSweepRunStartResponse = z.infer<
  typeof relationRefreshSweepRunStartResponseSchema
>;
export type RelationRefreshSweepRunStopResponse = z.infer<
  typeof relationRefreshSweepRunStopResponseSchema
>;
export type RelationRefreshSweepRunRestartResponse = z.infer<
  typeof relationRefreshSweepRunRestartResponseSchema
>;
export type RelationRefreshSweepRunDeleteResponse = z.infer<
  typeof relationRefreshSweepRunDeleteResponseSchema
>;
export type SyncJobsV2ListEntry = z.infer<typeof syncJobsV2ListEntrySchema>;
export type SyncWorkersResponse = z.infer<typeof syncWorkersResponseSchema>;
export type SyncJobsV2SummaryResponse = z.infer<typeof syncJobsV2SummaryResponseSchema>;
export type SyncJobsV2DiagnosticSeverity = z.infer<typeof syncJobsV2DiagnosticSeveritySchema>;
export type SyncJobsV2StaleReasonCode = z.infer<typeof syncJobsV2StaleReasonCodeSchema>;
export type WorkerHealthResponse = z.infer<typeof workerHealthResponseSchema>;
export type WorkerDiagnosticsResponse = z.infer<typeof workerDiagnosticsResponseSchema>;
export type WorkerEventsResponse = z.infer<typeof workerEventsResponseSchema>;
export type WorkerIncidentsResponse = z.infer<typeof workerIncidentsResponseSchema>;
export type SyncJobsMediaListEntry = z.infer<typeof syncJobsMediaListEntrySchema>;
export type SyncJobsMediaListResponse = z.infer<typeof syncJobsMediaListResponseSchema>;
export type SyncJobsV2RuntimeListEntry = z.infer<typeof syncJobsV2RuntimeListEntrySchema>;
export type SyncJobsV2RuntimeListResponse = z.infer<typeof syncJobsV2RuntimeListResponseSchema>;
export type SyncJobRunsRuntimeResponse = z.infer<typeof syncJobRunsRuntimeResponseSchema>;
export type SyncJobsV2SyncRunEntry = z.infer<typeof syncJobsV2SyncRunEntrySchema>;
export type SyncJobsV2SyncRunsResponse = z.infer<typeof syncJobsV2SyncRunsResponseSchema>;
export type SyncInventoryDataTypeOption = z.infer<typeof syncInventoryDataTypeOptionSchema>;
export type SyncInventoryDataTypeOptionsResponse = z.infer<
  typeof syncInventoryDataTypeOptionsResponseSchema
>;
export type SyncInventoryResponse = z.infer<typeof syncInventoryResponseSchema>;
export type SyncInventoryListSortBy = z.infer<typeof syncInventoryListSortBySchema>;
export type SyncInventoryListSortDir = z.infer<typeof syncInventoryListSortDirSchema>;
export type SyncInventoryListResponse = z.infer<typeof syncInventoryListResponseSchema>;
export type SyncInventoryColumnDetailsResponse = z.infer<
  typeof syncInventoryColumnDetailsResponseSchema
>;
export type SyncInventoryOptionSetOption = z.infer<typeof syncInventoryOptionSetOptionSchema>;
export type SyncInventoryOptionSetOptionsResponse = z.infer<
  typeof syncInventoryOptionSetOptionsResponseSchema
>;
export type CountAuditAcceptedResponse = z.infer<typeof countAuditAcceptedResponseSchema>;
export type RefreshColumnParityResponse = z.infer<typeof refreshColumnParityResponseSchema>;
export type DeltaSyncResponse = z.infer<typeof deltaSyncResponseSchema>;
